import { createContext, useContext, type ComponentType, type CSSProperties } from 'react';

/**
 * In-process editing events emitted by slots. The editor handles these
 * directly (bound values write through to document data).
 */
export interface SlotEditEvent {
  type: 'slot-edit';
  blockId: string;
  /** the rendered instance's key (blockId + repeat suffix) */
  blockKey: string;
  prop: string;
  /** for list props: the path inside the list value, e.g. "2.q" */
  itemPath?: string;
  value: string;
}

/** Structural edits on a list prop: add or remove an item, inline. */
export interface ListEditEvent {
  type: 'list-edit';
  blockId: string;
  blockKey: string;
  prop: string;
  op: 'insert' | 'remove';
  index: number;
  /** the blank item to insert (op: insert) */
  item?: Record<string, unknown>;
}

export type EditEvent = SlotEditEvent | ListEditEvent;

/**
 * The editing surface an app can supply for rich-text slots (e.g. TipTap).
 * Slots fall back to plain contentEditable when none is provided, so the
 * sdk stays dependency-free.
 */
export interface RichEditorProps {
  value: string;
  onCommit: (html: string) => void;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/** Document-wide edit state, provided once by the editor around the paper. */
export interface EditState {
  editMode: boolean;
  selectedId: string | null;
  hoveredId: string | null;
  /** deliver an edit event to the editor (no-op in read-only renders) */
  emit: (event: EditEvent) => void;
  /** optional rich-text editing surface for RichSlot */
  richEditor?: ComponentType<RichEditorProps> | undefined;
}

export const EditContext = createContext<EditState>({
  editMode: false,
  selectedId: null,
  hoveredId: null,
  emit: () => {},
});

export function useEditState(): EditState {
  return useContext(EditContext);
}

/** Per-rendered-block scope, provided around each block instance. */
export interface SectionScope {
  /** ResolvedBlock.key — unique per rendered instance */
  sectionKey: string;
  sectionId: string;
  /** the resolved props for this instance — slots read their value here */
  props: Record<string, unknown>;
  /**
   * When a list block is rendered as a pagination SLICE (only items
   * [offset, …] of the real array), the item slots see 0-based local indices
   * but must edit the real ones. Slots add this to their index. Default 0.
   */
  itemOffset?: number;
}

export const SectionContext = createContext<SectionScope | null>(null);

export function useSectionScope(): SectionScope {
  const scope = useContext(SectionContext);
  if (!scope) throw new Error('Slot rendered outside a block scope — wrap in SectionContext');
  return scope;
}

/** A resolved field value for the current block instance. */
export function useProp(name: string): unknown {
  return useSectionScope().props[name];
}

/** The resolved value as a display string ('' for null/undefined). */
export function useTextProp(name: string): string {
  const v = useProp(name);
  return v === null || v === undefined ? '' : String(v);
}
